import { KVPSSelect } from '@components/bid-buy/KVPSSelect';
import PurchaseAgentTransportSelect from '@components/bid-buy/PurchaseAgentTransportSelect';
import { useFilterSummary } from '@components/cars-table-next/filter/useFilterSummary';
import DeliveryAddressSelect from '@components/dealer-table/DeliveryAddressSelect';
import InfoTooltip from '@components/InfoTooltip';
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, InputLabel, Stack } from '@components/ui';
import {
  Button,
  Checkbox,
  CircularProgress,
  DrawerProps as MuiDrawerProps,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  OutlinedInput,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import { useCurrency } from 'src/hooks/useCurrency';
import { useGetDeliveryAddresses } from 'src/hooks/useDeliveryAddress';
import {
  getChangeStateErrorCodeKey,
  useChangePurchaseAgentState,
  useCreatePurchaseAgent,
  usePurchaseAgentDetail,
  useUpdatePurchaseAgent,
} from 'src/hooks/usePurchaseAgents';
import { SearchAgentDTOStrict, useSearchAgents } from 'src/hooks/useSearchAgents';
import useCustomSnackbarGlobal from 'src/hooks/useSnackbarGlobal';
import {
  CreatePurchaseAgentRequest,
  KvpsAndName,
  TransportationSelectionType,
  UpdatePurchaseAgentRequest,
} from 'src/modules/generated/api';
import {
  PURCHASE_AGENT_DATE_TIME_FORMAT,
  PURCHASE_AGENT_MAX_BUDGET_EUR,
  PURCHASE_AGENT_MIN_REMAINING_BUDGET_EUR,
} from 'src/modules/purchase-agent';
import { useSystemAgentLabel } from 'src/modules/system-agents';

import type { Merge } from 'type-fest';

const IntegerFormatInput = ({ inputRef, onChange, name, ...rest }: any) => (
  <NumberFormat
    {...rest}
    name={name}
    getInputRef={inputRef}
    onValueChange={(values) => {
      onChange({ target: { name, value: values.value } });
    }}
    isNumericString
    decimalScale={0}
    allowNegative={false}
    thousandSeparator="."
    decimalSeparator=","
  />
);

const useStyles = makeStyles((theme) => ({
  title: {
    color: theme.palette.intelligence.main,
    fontWeight: 'bold',
  },
  submitButton: {
    backgroundColor: theme.palette.intelligence.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.intelligence.dark,
    },
  },
  staleWarning: {
    color: theme.palette.warning.main,
  },
  confirmCheckbox: {
    marginTop: theme.spacing(1),
    alignItems: 'center',
  },
  newCycleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  confirmCheckboxLabel: {
    fontSize: '0.88rem',
    lineHeight: 1.4,
  },
}));

type PurchaseAgentDrawerProps = {
  open: boolean;
  onClose: () => void;
  initialSearchAgentId?: string;
  editPurchaseAgentId?: string;
};

type PurchaseAgentFormData = {
  name: string;
  linkedSearchAgentId: string;
  maxBudget: number | '';
  maxVehicleCount: number | '';
  transportationSelectionType: TransportationSelectionType | '';
  deliveryAddressId: string;
  kvps: string;
};

const defaultValues: PurchaseAgentFormData = {
  name: '',
  linkedSearchAgentId: '',
  maxBudget: '',
  maxVehicleCount: '',
  transportationSelectionType: '',
  deliveryAddressId: '',
  kvps: '',
};

export const PurchaseAgentDrawer = ({
  open,
  onClose,
  initialSearchAgentId,
  editPurchaseAgentId,
  ...drawerProps
}: Merge<Omit<MuiDrawerProps, 'open' | 'onClose'>, PurchaseAgentDrawerProps>) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const { data: searchAgents = [], isError: isSearchAgentsError } = useSearchAgents();
  const resolveLabel = useSystemAgentLabel();
  const getSearchAgentLabel = (sa: SearchAgentDTOStrict) => resolveLabel(sa.searchAgentDisplayName);
  const { data: deliveryAddressesData = [], isError: isDeliveryAddressesError } = useGetDeliveryAddresses();
  const { mutate: createPurchaseAgent, isLoading: isCreating } = useCreatePurchaseAgent();
  const { mutate: updatePurchaseAgent, isLoading: isUpdating } = useUpdatePurchaseAgent();
  const { mutate: changeState, isLoading: isStartingNewCycle } = useChangePurchaseAgentState();
  const snackbar = useCustomSnackbarGlobal();
  const { currency, convert, format } = useCurrency();
  const { data: filterSummary } = useFilterSummary({ summaryState: {} });
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [newCycleError, setNewCycleError] = useState<{
    reasonKey: ReturnType<typeof getChangeStateErrorCodeKey>;
  }>();

  const isEditMode = !!editPurchaseAgentId;
  const {
    data: detail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = usePurchaseAgentDetail(open ? editPurchaseAgentId : undefined);

  const dealerKvps = useMemo<KvpsAndName[]>(
    () =>
      (filterSummary?.kvps ?? [])
        .map((item) => item.item)
        .filter((item): item is string => item !== undefined)
        .map((kvps) => ({ kvps })),
    [filterSummary?.kvps],
  );

  // Stale data detection for edit mode
  const staleSearchAgent = useMemo(() => {
    if (!isEditMode || !detail || isSearchAgentsError) return false;
    const linkedId = detail.linkedSearchAgent?.id?.value;
    return !linkedId || !searchAgents.some((sa) => sa.id === linkedId);
  }, [isEditMode, detail, searchAgents, isSearchAgentsError]);

  const staleDeliveryAddress = useMemo(() => {
    if (!isEditMode || !detail || isDeliveryAddressesError) return false;
    const addressId = detail.transportation?.deliveryAddressId;
    return !!addressId && !deliveryAddressesData.some((da) => da.id === addressId);
  }, [isEditMode, detail, deliveryAddressesData, isDeliveryAddressesError]);

  const staleKvps = useMemo(() => {
    if (!isEditMode || !detail) return false;
    const kvpsValue = detail.kvps;
    return !!kvpsValue && !dealerKvps.some((k) => k.kvps === kvpsValue);
  }, [isEditMode, detail, dealerKvps]);

  const showSearchAgentAutocomplete = !isEditMode || staleSearchAgent;

  const budgetCurrency = isEditMode ? (detail?.limits?.maxBudget?.currency ?? 'EUR') : currency;

  const methods = useForm<PurchaseAgentFormData>({
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = methods;

  useEffect(() => {
    if (!open) return;

    if (isEditMode && detail) {
      reset({
        name: detail.name ?? '',
        linkedSearchAgentId: staleSearchAgent ? '' : (detail.linkedSearchAgent?.id?.value ?? ''),
        maxBudget: detail.limits?.maxBudget?.amount != null ? Math.round(detail.limits.maxBudget.amount) : '',
        maxVehicleCount: detail.limits?.maxVehicleCount ?? '',
        transportationSelectionType: detail.transportation?.transportationSelectionType ?? '',
        deliveryAddressId: staleDeliveryAddress ? '' : (detail.transportation?.deliveryAddressId ?? ''),
        kvps: staleKvps ? '' : (detail.kvps ?? ''),
      });
    } else if (!isEditMode) {
      reset({
        ...defaultValues,
        linkedSearchAgentId: initialSearchAgentId ?? '',
      });
      setConfirmationChecked(false);
    }
  }, [open, isEditMode, detail, initialSearchAgentId, reset, staleSearchAgent, staleDeliveryAddress, staleKvps]);

  // Separate from the effect above: that one re-runs on the detail refetch after a failed restart and
  // would wipe the error right away.
  useEffect(() => {
    if (open) setNewCycleError(undefined);
  }, [open]);

  // No form reset here: clearing the values while the drawer is still animating out re-validates the
  // emptied max budget and flashes its required error. The open effect above resets the form anyway.
  const handleClose = () => {
    onClose();
    setConfirmationChecked(false);
    setNewCycleError(undefined);
  };

  const linkedSearchAgentId = useWatch({ control, name: 'linkedSearchAgentId' });
  const selectedSearchAgent = useMemo(
    () => searchAgents.find((sa) => sa.id === linkedSearchAgentId) ?? null,
    [searchAgents, linkedSearchAgentId],
  );

  // Saving keeps the running limit cycle; only the explicit "start new cycle" button resets it.
  const onSubmit = (data: PurchaseAgentFormData, { startNewCycle = false } = {}) => {
    setNewCycleError(undefined);
    const budgetAmount = data.maxBudget !== '' ? Number(data.maxBudget) : undefined;

    if (isEditMode) {
      const payload: UpdatePurchaseAgentRequest = {
        name: data.name,
        kvps: data.kvps,
        limits: {
          maxBudget: {
            amount: budgetAmount,
            currency: budgetCurrency,
          },
          maxVehicleCount: data.maxVehicleCount !== '' ? Number(data.maxVehicleCount) : undefined,
        },
        transportation: {
          transportationSelectionType: data.transportationSelectionType as TransportationSelectionType,
          deliveryAddressId: data.deliveryAddressId,
        },
      };

      if (staleSearchAgent && data.linkedSearchAgentId) {
        payload.linkedSearchAgentId = data.linkedSearchAgentId;
      }

      const id = editPurchaseAgentId!;
      updatePurchaseAgent(
        { id, body: payload },
        {
          onSuccess: () => {
            if (!startNewCycle) {
              handleClose();
              return;
            }
            changeState(
              { id, action: 'activate' },
              {
                onSuccess: () => {
                  snackbar.showSuccess(t('purchaseAgent.messages.restartSuccess'));
                  handleClose();
                },
                onError: (error) => setNewCycleError({ reasonKey: getChangeStateErrorCodeKey(error) }),
              },
            );
          },
        },
      );
    } else {
      const payload: CreatePurchaseAgentRequest = {
        name: data.name,
        linkedSearchAgentId: data.linkedSearchAgentId,
        maxBudget: {
          amount: budgetAmount,
          currency: budgetCurrency,
        },
        maxVehicleCount: data.maxVehicleCount !== '' ? Number(data.maxVehicleCount) : undefined,
        transportationSelectionType: data.transportationSelectionType as TransportationSelectionType,
        deliveryAddressId: data.deliveryAddressId,
        kvps: data.kvps,
      };

      createPurchaseAgent(payload, {
        onSuccess: () => handleClose(),
      });
    }
  };

  const isSubmitting = isCreating || isUpdating || isStartingNewCycle;
  const isDetailPending = isEditMode && isDetailLoading;
  const isDetailFailed = isEditMode && isDetailError;

  return (
    <FormProvider {...methods}>
      <Drawer anchor="right" open={open} onClose={isSubmitting ? undefined : handleClose} {...drawerProps}>
        <DrawerHeader>
          <Typography className={classes.title}>
            {isEditMode ? t('purchaseAgent.drawer.editTitle') : t('purchaseAgent.drawer.title')}
          </Typography>
        </DrawerHeader>
        <DrawerContent>
          {isDetailPending ? (
            <Grid container justifyContent="center" alignItems="center" style={{ height: '100%' }}>
              <CircularProgress />
            </Grid>
          ) : null}
          {isDetailFailed ? <Typography color="error">{t('purchaseAgent.drawer.detailLoadError')}</Typography> : null}
          <form
            id="purchase-agent-form"
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit((data) => onSubmit(data))}
            style={isDetailPending || isDetailFailed ? { display: 'none' } : undefined}
          >
            <Stack spacing={2}>
              <FormControl size="small" fullWidth variant="outlined">
                <InputLabel required>{t('purchaseAgent.form.name')}</InputLabel>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: t('validation.required') }}
                  render={({ field: { ref, ...field } }) => <OutlinedInput inputRef={ref} {...field} />}
                />
                {errors.name ? <FormHelperText error>{errors.name.message}</FormHelperText> : null}
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel required>{t('purchaseAgent.form.searchAgent')}</InputLabel>
                {showSearchAgentAutocomplete ? (
                  <>
                    <Controller
                      name="linkedSearchAgentId"
                      control={control}
                      rules={{ required: t('validation.required') }}
                      render={({ field }) => (
                        <Autocomplete<SearchAgentDTOStrict>
                          options={[...searchAgents].sort((a, b) =>
                            getSearchAgentLabel(a).localeCompare(getSearchAgentLabel(b)),
                          )}
                          getOptionLabel={getSearchAgentLabel}
                          value={selectedSearchAgent}
                          onChange={(_e, newValue) => {
                            field.onChange(newValue?.id ?? '');
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              size="small"
                              placeholder={t('purchaseAgent.form.searchAgent')}
                            />
                          )}
                        />
                      )}
                    />
                    {staleSearchAgent && (
                      <FormHelperText className={classes.staleWarning}>
                        {t('purchaseAgent.form.staleWarning')}
                      </FormHelperText>
                    )}
                    {errors.linkedSearchAgentId ? (
                      <FormHelperText error>{errors.linkedSearchAgentId.message}</FormHelperText>
                    ) : null}
                  </>
                ) : (
                  <OutlinedInput disabled value={resolveLabel(detail?.linkedSearchAgent?.name ?? '')} />
                )}
              </FormControl>

              <FormControl size="small" fullWidth variant="outlined">
                <InputLabel required>{t('purchaseAgent.form.maxBudget', { currency: budgetCurrency })}</InputLabel>
                <Controller
                  name="maxBudget"
                  control={control}
                  rules={{
                    required: t('validation.required'),
                    validate: {
                      integer: (v) => v === '' || Number.isInteger(Number(v)) || t('validation.integer'),
                      min: (v) => {
                        if (v === '') return true;
                        const minBudget = convert(PURCHASE_AGENT_MIN_REMAINING_BUDGET_EUR, {
                          from: 'EUR',
                          to: budgetCurrency,
                        });
                        return (
                          Number(v) > minBudget ||
                          t('purchaseAgent.form.minBudget', { min: format(minBudget, { to: budgetCurrency }) })
                        );
                      },
                      max: (v) => {
                        if (v === '') return true;
                        const maxBudget = convert(PURCHASE_AGENT_MAX_BUDGET_EUR, { from: 'EUR', to: budgetCurrency });
                        return (
                          Number(v) <= maxBudget ||
                          t('validation.maxValue', { max: format(maxBudget, { to: budgetCurrency }) })
                        );
                      },
                    },
                  }}
                  render={({ field: { ref, ...field } }) => (
                    <OutlinedInput inputRef={ref} {...field} inputComponent={IntegerFormatInput} />
                  )}
                />
                {errors.maxBudget ? (
                  <FormHelperText error>{errors.maxBudget.message}</FormHelperText>
                ) : budgetCurrency !== currency ? (
                  <FormHelperText>{t('purchaseAgent.form.budgetCurrencyHint', { budgetCurrency })}</FormHelperText>
                ) : null}
              </FormControl>

              <FormControl size="small" fullWidth variant="outlined">
                <InputLabel>{t('purchaseAgent.form.maxCount')}</InputLabel>
                <Controller
                  name="maxVehicleCount"
                  control={control}
                  rules={{
                    validate: {
                      integer: (v) => v === '' || Number.isInteger(Number(v)) || t('validation.integer'),
                      max: (v) => v === '' || Number(v) <= 100 || t('validation.maxValue', { max: 100 }),
                    },
                  }}
                  render={({ field: { ref, ...field } }) => (
                    <OutlinedInput inputRef={ref} {...field} inputComponent={IntegerFormatInput} />
                  )}
                />
                {errors.maxVehicleCount ? (
                  <FormHelperText error>{errors.maxVehicleCount.message}</FormHelperText>
                ) : null}
              </FormControl>

              <FormControl size="small" fullWidth error={!!errors.transportationSelectionType}>
                <InputLabel required>{t('purchaseAgent.form.transportProvider')}</InputLabel>
                <PurchaseAgentTransportSelect
                  control={control}
                  name="transportationSelectionType"
                  rules={{
                    required: t('validation.required'),
                    validate: (v: string) => v !== '' || t('validation.required'),
                  }}
                />
                {errors.transportationSelectionType ? (
                  <FormHelperText error>{errors.transportationSelectionType.message}</FormHelperText>
                ) : null}
              </FormControl>

              <FormControl size="small" fullWidth error={!!errors.deliveryAddressId}>
                <InputLabel required>{t('purchaseAgent.form.deliveryAddress')}</InputLabel>
                <DeliveryAddressSelect
                  control={control}
                  name="deliveryAddressId"
                  deliveryAddresses={deliveryAddressesData}
                  fullWidth
                  rules={{ required: t('validation.required') }}
                />
                {staleDeliveryAddress && (
                  <FormHelperText className={classes.staleWarning}>
                    {t('purchaseAgent.form.staleWarning')}
                  </FormHelperText>
                )}
                {errors.deliveryAddressId ? (
                  <FormHelperText error>{errors.deliveryAddressId.message}</FormHelperText>
                ) : null}
              </FormControl>

              <FormControl size="small" fullWidth error={!!errors.kvps}>
                <InputLabel required>{t('purchaseAgent.form.kvps')}</InputLabel>
                <KVPSSelect
                  kvps={dealerKvps}
                  rules={{
                    required: t('validation.required'),
                    validate: (v: string) => v !== '' || t('validation.required'),
                  }}
                />
                {staleKvps && (
                  <FormHelperText className={classes.staleWarning}>
                    {t('purchaseAgent.form.staleWarning')}
                  </FormHelperText>
                )}
                {errors.kvps ? <FormHelperText error>{errors.kvps.message}</FormHelperText> : null}
              </FormControl>

              {!isEditMode && (
                <FormControlLabel
                  className={classes.confirmCheckbox}
                  control={
                    <Checkbox
                      checked={confirmationChecked}
                      onChange={(e) => setConfirmationChecked(e.target.checked)}
                    />
                  }
                  label={
                    <span className={classes.confirmCheckboxLabel}>
                      {t('purchaseAgent.form.confirmCheckbox')}
                      <InfoTooltip xsIcon title={t('purchaseAgent.form.confirmCheckboxHint')} />
                    </span>
                  }
                />
              )}
            </Stack>
          </form>
        </DrawerContent>
        <DrawerFooter>
          {isEditMode && detail?.lastModified && (
            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBlockEnd: 8 }}>
              {t('purchaseAgent.drawer.lastModified', {
                date: new Date(detail.lastModified).toLocaleString(i18n.language, PURCHASE_AGENT_DATE_TIME_FORMAT),
              })}
            </Typography>
          )}
          {newCycleError && (
            <Typography variant="caption" color="error" style={{ display: 'block', marginBlockEnd: 8 }}>
              {t('purchaseAgent.messages.restartError')}
              {newCycleError.reasonKey ? ` ${t(newCycleError.reasonKey)}` : null}
            </Typography>
          )}
          <Grid container justifyContent="space-between" alignItems="center">
            <Button size="small" variant="outlined" color="secondary" disabled={isSubmitting} onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="purchase-agent-form"
              size="small"
              variant="contained"
              className={classes.submitButton}
              disabled={isSubmitting || isDetailPending || isDetailFailed || (!isEditMode && !confirmationChecked)}
            >
              {t('common.save')}
            </Button>
          </Grid>
          {isEditMode && (
            <div className={classes.newCycleRow}>
              <Button
                size="small"
                variant="outlined"
                fullWidth
                disabled={isSubmitting || isDetailPending || isDetailFailed}
                onClick={handleSubmit((data) => onSubmit(data, { startNewCycle: true }))}
              >
                {t('purchaseAgent.drawer.saveAndRestart')}
              </Button>
              <InfoTooltip xsIcon title={t('purchaseAgent.drawer.saveAndRestartHint')} />
            </div>
          )}
        </DrawerFooter>
      </Drawer>
    </FormProvider>
  );
};
